import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase_config';
import { CircularProgress, Box } from '@mui/material';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Wrap the token fetch in a try/catch. 
          // If it fails, the code will still proceed to setLoading(false).
          const idTokenResult = await user.getIdTokenResult(true);
          setIsAdmin(!!idTokenResult.claims.admin);
        } catch (error) {
          console.error("Error fetching admin claims:", error);
          setIsAdmin(false); 
        }
      } else {
        setIsAdmin(false);
      }
      
      // Guaranteed to execute now
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      // Wait for the sign-in to complete
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential;
      // Note: We don't need to setLoading(false) here on success, 
      // because onAuthStateChanged will automatically catch the state change and do it.
    } catch (error) {
      // FIX: If login fails, manually reset the loading state so the app doesn't hang
      setLoading(false);
      throw error; // Re-throw so your login component can display an error message
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const value = {
    currentUser,
    isAdmin,
    loading,
    login,
    logout,
  };
  
  // Optional: You imported Material UI components but didn't use them. 
  // You can use them here to block the whole app from rendering while initially loading!
  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};